import { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";
import type { MetricWithAttribution } from "web-vitals/attribution";

import { AppSettingStore } from "./AppSettingStore";
import { MyStore } from "./MyStore";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { reportWebVitals } from "reportWebVitals";
import { ORPCError, ValidationError } from "@orpc/contract";
import { isCancelledError } from "@tanstack/react-query";
import { ZodiosError } from "@zodios/core";
import { isAxiosError } from "axios";
import { LogCategory } from "Enumerations/LogCategory";
import type { RootOptions } from "react-dom/client";
import { isServiceErrorDetails } from "Shared/Utils/ServiceErrorDetails";
import { startCustomerMetrics } from "Core/Telemetry/CustomerMetrics";
import { redactSensitiveTelemetry } from "Core/Telemetry/redactSensitiveData";

// Decided once per page load. A page load sends at most a handful of metrics, which is small next to the per-API-call telemetry.
const WEB_VITALS_SAMPLE_RATE = 1;

const isSampled = (rate: number) => (crypto.getRandomValues(new Uint32Array(1))[0] ?? 0) / 0x100000000 < rate;

const getWebVitalTarget = (metric: MetricWithAttribution): string => {
    switch (metric.name) {
        case "CLS":
            return metric.attribution.largestShiftTarget ?? "";
        case "INP":
            return metric.attribution.interactionTarget ?? "";
        case "LCP":
            return metric.attribution.target ?? "";
        default:
            return "";
    }
};

interface LogStoreState {
    useAppInsights: boolean;
    appInsights: ApplicationInsights | null;
    initialized: boolean;
    applicationInsightsConnectionString: string;
}

type GlobalErrorSource = "ReactUncaught" | "ReactCaught" | "UnhandledRejection";
type ExceptionSink = (exception: Error, properties: Record<string, string>) => void;

const globalErrorTagIds: Record<GlobalErrorSource, string> = {
    ReactUncaught: "086fb24e-3f94-4ac5-b3ff-4778986c8968",
    ReactCaught: "1657ac83-874a-4cfd-8c47-25ebba58b991",
    UnhandledRejection: "6318e78d-3f41-415b-a700-59ecf25f54db"
};

// Errors caught by an error boundary are logged after its fallback has mounted. A fallback that logs the error itself
// (the full-page ErrorBoundary does, with the Tag ID and correlation ID it shows the user) has marked it by then.
const caughtErrorDelayMs = 1000;
const maxGlobalErrorsPerPage = 50;
const maxPendingGlobalErrors = 10;
const maxErrorMessageLength = 1024;
const maxComponentStackLength = 2000;

const noiseErrorPattern = /resizeobserver loop/i;
const secretPatterns: [RegExp, string][] = [
    [/\bbearer\s+[\w+./=~-]+/gi, "Bearer [redacted]"],
    [/\beyJ(?:[\w-]+\.){2}[\w-]*/g, "[redacted-jwt]"],
    [/([#&?](?:access_token|id_token|refresh_token|token|code|client_secret|sig|signature|key|password)=)[^\s"#&']*/gi, "$1[redacted]"]
];

const reportedErrors = new WeakSet<object>();
const pendingGlobalErrors: [Error, Record<string, string>][] = [];
// Undefined until the LogStore initializes (errors wait in pendingGlobalErrors); null when App Insights is off.
let globalErrorSink: ExceptionSink | null | undefined;
let globalErrorCount = 0;

const isObject = (value: unknown): value is object => typeof value === "object" && value !== null;

const getErrorMessage = (value: unknown): string | undefined => {
    if (typeof value === "string") {
        return value;
    }
    return isObject(value) && "message" in value && typeof value.message === "string" ? value.message : undefined;
};

const truncate = (text: string, maxLength: number) => (text.length > maxLength ? text.substring(0, maxLength) : text);

const redactSecrets = (text: string) => secretPatterns.reduce((result, [pattern, replacement]) => result.replace(pattern, replacement), text);

const isNoiseError = (value: unknown) =>
    isCancelledError(value) || (isObject(value) && "name" in value && value.name === "AbortError") || noiseErrorPattern.test(getErrorMessage(value) ?? "");

// The API layer (oRPC, Zodios and axios interceptors) already logs failed calls: as exceptions, or as traces for 401, 403, 404 and 429.
// These errors also carry the request input and headers, which must not be sent again.
const isApiError = (value: unknown) =>
    value instanceof ORPCError || value instanceof ValidationError || value instanceof ZodiosError || isAxiosError(value) || isServiceErrorDetails(value);

// Only the name, message and stack are sent, with tokens redacted. Other fields of the thrown value are never serialized.
const toSafeError = (value: unknown): Error => {
    const message = getErrorMessage(value);
    const safeError = new Error(
        message === undefined ? `Non-error value: ${Object.prototype.toString.call(value)}` : truncate(redactSecrets(message), maxErrorMessageLength)
    );
    if (value instanceof Error) {
        safeError.name = value.name;
        safeError.stack = value.stack === undefined ? undefined : redactSecrets(value.stack);
    } else {
        safeError.stack = undefined;
    }
    return safeError;
};

const markErrorReported = (error: unknown) => {
    if (isObject(error)) {
        reportedErrors.add(error);
    }
};

const setGlobalErrorSink = (sink: ExceptionSink | null) => {
    globalErrorSink = sink;
    const pending = pendingGlobalErrors.splice(0);
    if (sink) {
        pending.forEach(([exception, properties]) => sink(exception, properties));
    }
};

const captureGlobalError = (error: unknown, source: GlobalErrorSource, route: string, componentStack?: string) => {
    try {
        if (globalErrorSink === null || isNoiseError(error) || isApiError(error)) {
            return;
        }
        if (isObject(error)) {
            if (reportedErrors.has(error)) {
                return;
            }
            reportedErrors.add(error);
        }
        if (globalErrorCount >= maxGlobalErrorsPerPage) {
            return;
        }
        globalErrorCount++;
        const properties: Record<string, string> = {
            TagId: globalErrorTagIds[source],
            Category: LogCategory.Core,
            LogLayer: "Client",
            Source: source,
            Route: route
        };
        if (componentStack) {
            properties.ComponentStack = truncate(componentStack.trim(), maxComponentStackLength);
        }
        const exception = toSafeError(error);
        if (globalErrorSink) {
            globalErrorSink(exception, properties);
        } else if (pendingGlobalErrors.length < maxPendingGlobalErrors) {
            pendingGlobalErrors.push([exception, properties]);
        }
    } catch (captureFailure) {
        console.warn("Global error capture failed", captureFailure);
    }
};

/**
 * React root error handlers for createRoot. With onUncaughtError set, React no longer re-reports uncaught errors
 * to window.onerror, so App Insights' own window.onerror hook doesn't log them a second time.
 */
export const rootErrorHandlers: Pick<RootOptions, "onUncaughtError" | "onCaughtError"> = {
    onUncaughtError: (error, errorInfo) => {
        console.error(error);
        captureGlobalError(error, "ReactUncaught", window.location.pathname, errorInfo.componentStack);
    },
    onCaughtError: (error, errorInfo) => {
        console.error(error);
        const route = window.location.pathname;
        window.setTimeout(() => captureGlobalError(error, "ReactCaught", route, errorInfo.componentStack), caughtErrorDelayMs);
    }
};

export const captureUnhandledRejections = () => {
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) =>
        captureGlobalError(event.reason, "UnhandledRejection", window.location.pathname)
    );
};

// App Insights logs window errors itself, including the ResizeObserver loop errors browsers raise. Drop those.
const dropNoisyExceptions = (envelope: ITelemetryItem) => {
    if (envelope.baseType !== "ExceptionData") {
        return true;
    }
    const exceptions: unknown = envelope.baseData?.exceptions;
    const message = (Array.isArray(exceptions) ? getErrorMessage(exceptions[0]) : undefined) ?? getErrorMessage(envelope.baseData?.properties) ?? "";
    return !noiseErrorPattern.test(message);
};

const duplicatePageViewWindowMs = 2000;

export const LogStore = (appSettingStore: ReturnType<typeof AppSettingStore>, myStore: ReturnType<typeof MyStore>) => {
    const appSettings = appSettingStore.settings;
    const reactPlugin = new ReactPlugin();
    // useLogging logs exceptions through this plugin. Mark them so the global error capture doesn't log them again.
    const trackReactPluginException = reactPlugin.trackException.bind(reactPlugin);
    reactPlugin.trackException = (exception, customProperties) => {
        markErrorReported(exception.exception);
        trackReactPluginException(exception, customProperties);
    };
    const state: LogStoreState = {
        useAppInsights: false,
        appInsights: null,
        initialized: false,
        applicationInsightsConnectionString: ""
    };
    const appContext: Map<string, { [key: string]: string }> = new Map();
    let lastPageView: { path: string; time: number } | undefined;

    const checkUsingAppInsight = () => {
        if (state.useAppInsights && !state.initialized) {
            console.warn("Should not use AppInsight before initializing.");
        }
        return state.useAppInsights && state.initialized;
    };

    const addAppInfoToLog = (envelope: ITelemetryItem) => {
        envelope.data = envelope.data || {};
        const currentApp = appSettingStore.currentApp;
        envelope.data.AppName = currentApp.title;
        envelope.data.AppOwner = currentApp.owner.toLowerCase();
        envelope.data.AppTeam = currentApp.team;
        const currentAppContext = appContext.get(currentApp.id);
        if (currentAppContext) {
            envelope.data = { ...envelope.data, ...currentAppContext };
        }
    };

    const addUserInfoToLog = (envelope: ITelemetryItem) => {
        const myManagers = myStore.managers;
        envelope.data = envelope.data || {};
        envelope.data.UserOCERole = myStore.userOCERole;
        if (myManagers.length > 0) {
            envelope.data.UserManagers = myManagers;
        }
        envelope.data.UserRoles = myStore.profile.Roles;
    };

    // Auto route tracking logs a page view for every history change, so URL syncs and redirects right after a navigation
    // log the same page several times. Keep only the first view of a path within the window.
    const dropDuplicatePageView = (envelope: ITelemetryItem) => {
        if (envelope.baseType !== "PageviewData") {
            return true;
        }
        const uri = String(envelope.baseData?.uri ?? window.location.href);
        const path = new URL(uri, window.location.origin).pathname;
        const now = Date.now();
        const isDuplicate = lastPageView?.path === path && now - lastPageView.time < duplicatePageViewWindowMs;
        lastPageView = { path, time: now };
        return !isDuplicate;
    };

    const trackWebVital = (metric: MetricWithAttribution) => {
        state.appInsights?.trackMetric(
            { name: `WebVitals.${metric.name}`, average: metric.value },
            {
                Rating: metric.rating,
                MetricId: metric.id,
                NavigationType: metric.navigationType,
                Route: window.location.pathname,
                Target: getWebVitalTarget(metric)
            }
        );
    };

    const continueInitialization = () => {
        if (state.initialized) {
            console.warn("LogStore should not be initialized twice");
            return;
        }
        if (state.useAppInsights) {
            state.appInsights = new ApplicationInsights({
                config: {
                    connectionString: state.applicationInsightsConnectionString,
                    enableCorsCorrelation: true,
                    enableAutoRouteTracking: true,
                    extensions: [reactPlugin],
                    // Opt out of SDK Stats (on by default since 3.4.3).
                    // 2 is FeatureOptInMode.disable, an ambient const enum that can't be imported with isolatedModules.
                    featureOptIn: {
                        SdkStats: { mode: 2 },
                        sdkStats: { mode: 2 }
                    }
                }
            });
            state.appInsights.loadAppInsights();
            if (appSettings.m365appVersion) {
                state.appInsights.context.application.ver = appSettings.m365appVersion;
            }
            startCustomerMetrics(state.appInsights);
            state.appInsights.addTelemetryInitializer(dropDuplicatePageView);
            state.appInsights.addTelemetryInitializer(dropNoisyExceptions);
            state.appInsights.addTelemetryInitializer(addAppInfoToLog);
            state.appInsights.addTelemetryInitializer(addUserInfoToLog);
            // Last, so it also sees what the initializers above add.
            state.appInsights.addTelemetryInitializer(redactSensitiveTelemetry);
            setGlobalErrorSink((exception, properties) => state.appInsights?.trackException({ exception, properties }));
            if (isSampled(WEB_VITALS_SAMPLE_RATE)) {
                reportWebVitals(trackWebVital);
            }
        } else {
            setGlobalErrorSink(null);
        }
        state.initialized = true;
    };

    return {
        initialize() {
            if (appSettings.appInsightsLog?.toLowerCase() === "true" && isValidInstrumentationKey(appSettings.applicationInsightsConnectionString)) {
                state.useAppInsights = true;
                state.applicationInsightsConnectionString = appSettings.applicationInsightsConnectionString ?? "";
            } else {
                state.useAppInsights = false;
                console.warn("AppInsights logging is disabled or invalid instrumentation key.");
            }
            continueInitialization();
        },
        reactPlugin,
        setAuthenticatedUserContext: (userId: string) => {
            if (checkUsingAppInsight() && state.appInsights) {
                state.appInsights.setAuthenticatedUserContext(userId);
                state.appInsights.context.user.id = userId;
                state.appInsights.trackPageView({ uri: window.location.pathname });
            }
        },
        addAppContext: (context: { [key: string]: string }) => {
            const appKey = appSettingStore.currentApp.id;
            const newContext = { ...appContext.get(appKey), ...context };
            appContext.set(appKey, newContext);
        },
        removeAppContext: (keys: string[]) => {
            const appKey = appSettingStore.currentApp.id;
            const context = appContext.get(appKey);
            if (context) {
                const newContext: { [key: string]: string } = Object.fromEntries<string>(Object.entries(context).filter(([key]) => !keys.includes(key)));
                appContext.set(appKey, newContext);
            }
        },
        lastErrorCorrelationId: ""
    };
};

const isValidInstrumentationKey = (instrumentationKey: string | undefined) => {
    if (!instrumentationKey) {
        return false;
    }

    return /^instrumentationkey=[\da-f]{8}(?:-[\da-f]{4}){2}-[89ab][\da-f]{3}-[\da-f]{12}(;.*)?$/i.test(instrumentationKey);
};

export type TLogStore = ReturnType<typeof LogStore>;
